/**
 * Permission dropdown for the Share dialog — same UX as the Member Access
 * dialog's picker (a portal-positioned dropdown, not a native select), with
 * the same labels. Capped to the sharer's own access on the folder:
 * effective access is min(your permission, what you grant), so levels above
 * yours are disabled.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Share2, Trash2 } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

export type SharePermission = 'readcopy' | 'readwrite' | 'admin'

export const SHARE_PERM_LABELS: Record<SharePermission, string> = {
  readcopy: 'Read-only',
  readwrite: 'Read & Write',
  admin: 'Manage & Edit',
}

// 'admin' (Manage & Edit) is intentionally NOT offered for outsider shares —
// granting full management to an external org is out of scope here.
const SHARE_OPTIONS: SharePermission[] = ['readcopy', 'readwrite']

// Strength ranks, for capping to the sharer's own access.
const RANK: Record<string, number> = {
  none: 0, hidden: 0, readcopy: 1, readwrite: 2, admin: 3, owner: 4,
}

/**
 * The grantable options for a sharer whose own access on the folder is
 * `maxPermission`. Edit (readwrite) is OWNER/ADMIN-only — parity with the
 * share-link `canGrantEdit` gate: a member sharing own content, or anyone
 * re-sharing inbound `.shared` (mount ceiling is readwrite, never admin), can
 * grant Read-only. So `readwrite` is omitted unless the sharer is admin/owner.
 * Undefined `maxPermission` = no folder cap (e.g. the owner's Add-to-Drive
 * approval popup, where edit is legitimate); the server enforces it regardless.
 */
function grantableShareOptions(maxPermission?: string): SharePermission[] {
  const canGrantEdit = maxPermission == null || (RANK[maxPermission] ?? 0) >= RANK.admin
  return canGrantEdit ? SHARE_OPTIONS : SHARE_OPTIONS.filter(o => o !== 'readwrite')
}

export function SharePermissionPicker({ value, onChange, maxPermission, disabled = false }: {
  value: SharePermission
  onChange: (v: SharePermission) => void
  /** The caller's effective permission on the folder — options stronger
   *  than this are disabled (you can't grant more than you have). Undefined
   *  ⇒ no cap. */
  maxPermission?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const myRank = maxPermission != null ? (RANK[maxPermission] ?? 99) : 99

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => {
    if (open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.right - 180 })
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`flex items-center gap-1 text-sm transition-colors ${
          disabled ? 'themed-text-muted opacity-50 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 cursor-pointer'
        }`}
      >
        {SHARE_PERM_LABELS[value]}
        {!disabled && <ChevronDown size={14} />}
      </button>
      {open && createPortal(
        <div ref={dropdownRef} className={`fixed z-[320] ${themeClass} themed-bg-tertiary border themed-border rounded-lg shadow-xl py-1 w-[180px]`} style={{ top: pos.top, left: pos.left }}>
          {grantableShareOptions(maxPermission).map(opt => {
            const optDisabled = RANK[opt] > myRank
            return (
              <button
                key={opt}
                disabled={optDisabled}
                onClick={() => { onChange(opt); setOpen(false) }}
                title={optDisabled ? "You can't grant more access than you have on this folder" : undefined}
                className={`w-full px-3 py-1.5 text-left text-sm transition-colors ${
                  value === opt ? 'text-blue-400 font-medium' : 'themed-text'
                } ${optDisabled ? 'opacity-40 cursor-not-allowed' : 'themed-bg-hover'}`}
              >
                {SHARE_PERM_LABELS[opt]}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </>
  )
}

/**
 * Reusable popover surface shared by `RecipientAccessPicker` (this file)
 * and `WhoHasAccessDialog`'s 3-dot CardActionsMenu. Renders:
 *
 *   [optional Inherit row → divider]
 *   PERMISSION header + N option rows
 *   divider
 *   RE-SHARING header + Allow/No-sharing rows
 *   [optional divider + Confirm button]
 *
 * Same visual treatment as RecipientAccessPicker's popover (themed-bg-primary,
 * ring + shadow, w-[168px], text-[13px], Check-icon prefix, text-blue-400
 * font-medium for selected) — passing `confirmAction` switches to the
 * staged-changes mode (Confirm at the bottom); omitting it = immediate
 * apply on click.
 */
export function AccessSettingsPopover({
  themeClass,
  pos,
  dropdownRef,
  permissionOptions,
  permissionLabels,
  selectedPermission,
  disabledOptions,
  onSelectPermission,
  inherit,
  sharingAllowed,
  onSetSharing,
  confirmAction,
  removeAction,
}: {
  themeClass: string
  pos: { top: number; left: number }
  dropdownRef: React.RefObject<HTMLDivElement | null>
  permissionOptions: readonly string[]
  permissionLabels: Record<string, string>
  selectedPermission: string | null
  disabledOptions?: ReadonlySet<string>
  onSelectPermission: (opt: string) => void
  /** Optional "Inherit (X)" row at the top — MemberAccessDialog pattern.
   *  `selected` highlights the row; `onClick` reverts an explicit grant
   *  to inheritance. */
  inherit?: { label: string; selected: boolean; onClick: () => void }
  /** Omit BOTH to hide the Re-sharing section — e.g. keybook grants,
   *  where the manage level itself carries the share authority. */
  sharingAllowed?: boolean
  onSetSharing?: (allowed: boolean) => void
  /** Optional Confirm button (RecipientAccessPicker uses this for staged
   *  edit). Omit for immediate-apply menus. */
  confirmAction?: { label: string; onClick: () => void }
  /** Optional destructive "Remove" row at the bottom (red) — folds a card's
   *  standalone Remove button into this popover. */
  removeAction?: { label: string; onClick: () => void }
}) {
  return (
    <div
      ref={dropdownRef}
      className={`fixed z-[320] ${themeClass} themed-bg-primary border themed-border ring-1 ring-black/30 rounded-lg shadow-2xl py-0.5 w-[168px]`}
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="px-2.5 pt-0.5 text-[9px] themed-text-muted uppercase tracking-wider">Permission</div>
      {inherit && (
        <button
          onClick={inherit.onClick}
          className={`w-full px-2.5 py-1 text-left text-[13px] flex items-center gap-1.5 transition-colors themed-bg-hover ${
            inherit.selected ? 'text-blue-400 font-medium' : 'themed-text'
          }`}
        >
          <Check size={11} className={`shrink-0 ${inherit.selected ? 'opacity-100' : 'opacity-0'}`} />
          {inherit.label}
        </button>
      )}
      {permissionOptions.map(opt => {
        const optDisabled = disabledOptions?.has(opt) ?? false
        const isSelected = selectedPermission === opt
        return (
          <button
            key={opt}
            disabled={optDisabled}
            onClick={() => onSelectPermission(opt)}
            title={optDisabled ? "You can't grant more access than you have on this folder" : undefined}
            className={`w-full px-2.5 py-1 text-left text-[13px] flex items-center gap-1.5 transition-colors ${
              isSelected ? 'text-blue-400 font-medium' : 'themed-text'
            } ${optDisabled ? 'opacity-40 cursor-not-allowed' : 'themed-bg-hover'}`}
          >
            <Check size={11} className={`shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
            {permissionLabels[opt] ?? opt}
          </button>
        )
      })}
      {onSetSharing && (
        <>
          <div className="border-t themed-border my-0.5" />
          <div className="px-2.5 text-[9px] themed-text-muted uppercase tracking-wider flex items-center gap-1">
            <Share2 size={9} /> Re-sharing
          </div>
          {[{ v: true, label: 'Allow sharing' }, { v: false, label: 'No sharing' }].map(opt => (
            <button
              key={opt.label}
              onClick={() => onSetSharing(opt.v)}
              className={`w-full px-2.5 py-1 text-left text-[13px] flex items-center gap-1.5 transition-colors themed-bg-hover ${
                sharingAllowed === opt.v ? 'text-blue-400 font-medium' : 'themed-text'
              }`}
            >
              <Check size={11} className={`shrink-0 ${sharingAllowed === opt.v ? 'opacity-100' : 'opacity-0'}`} />
              {opt.label}
            </button>
          ))}
        </>
      )}
      {removeAction && (
        <>
          <div className="border-t themed-border my-0.5" />
          <button
            onClick={removeAction.onClick}
            className="w-full px-2.5 py-1 text-left text-[13px] flex items-center gap-1.5 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={11} className="shrink-0" />
            {removeAction.label}
          </button>
        </>
      )}
      {confirmAction && (
        <>
          <div className="border-t themed-border my-0.5" />
          <div className="px-1.5 pt-0.5 pb-1">
            <button
              onClick={confirmAction.onClick}
              className="w-full px-2.5 py-1 text-[13px] bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              {confirmAction.label}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/** Combined access picker for a Share recipient. The trigger is a single
 *  clickable block (no border) showing the current permission ("Read-only")
 *  on top and the re-sharing state ("Allow sharing" / "No sharing") beneath.
 *  Clicking it opens one popover with the permission options AND the
 *  re-sharing choice; changes apply instantly (no Confirm step) — each
 *  click fires `onChange` with the full (permission, allowSharing) pair,
 *  matching the immediate-apply UX of WhoHasAccessDialog's CardActionsMenu. */
export function RecipientAccessPicker({ permission, allowSharing, maxPermission, options, onChange, onRemove, removeLabel = 'Remove', readOnly, onReadOnlyClick }: {
  permission: SharePermission
  allowSharing: boolean
  /** Caller's effective permission on the folder — caps the options. */
  maxPermission?: string
  /**
   * Override the offered tiers. The default set is the OUTSIDER one
   * (`readcopy`/`readwrite` — 'admin' is out of scope for cross-tenant
   * shares); INTERNAL callers (ShareInternallyDialog) pass the full set
   * including 'admin', which is a normal internal grant.
   */
  options?: SharePermission[]
  /** Called on each click with the new combined state. For a new card the
   *  parent just updates local state; for an already-shared recipient it
   *  runs the revoke + re-grant. The parent decides whether a no-op
   *  (same perm + same share) should short-circuit. */
  onChange: (permission: SharePermission, allowSharing: boolean) => void
  /** When set, the popover shows a red "Remove" row at the bottom. Omit for
   *  cards that can't be removed here (e.g. reshare-by-you records). */
  onRemove?: () => void
  /** Label for that destructive row (default 'Remove'). Pending cards pass
   *  'Cancel request' / 'Cancel reshare' so the menu reads as a withdrawal. */
  removeLabel?: string
  /** Read-only: clicking the trigger does NOT open the popover — it calls
   *  onReadOnlyClick (e.g. a toast) instead. For records the viewer can't change
   *  here (a reshare-by-you card — only the content owner holds the grant). */
  readOnly?: boolean
  onReadOnlyClick?: () => void
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const myRank = maxPermission != null ? (RANK[maxPermission] ?? 99) : 99

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current?.contains(e.target as Node)) return
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Position below the trigger, but keep the whole popover INSIDE the parent
  // Share modal so it never hangs off the bottom of the dialog. We clamp the
  // top to the modal's bounds (falling back to the viewport when there's no
  // modal ancestor). useLayoutEffect + the measured height runs before paint,
  // so there's no flicker.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return
    const r = wrapperRef.current.getBoundingClientRect()
    const h = dropdownRef.current?.offsetHeight ?? 220
    const modal = wrapperRef.current.closest('[data-share-modal]')?.getBoundingClientRect()
    const POP_W = 168
    let top = r.bottom + 4
    if (modal) {
      const minTop = modal.top + 8
      const maxTop = modal.bottom - h - 8
      top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop))
    } else if (window.innerHeight - r.bottom < h + 12) {
      top = Math.max(8, r.top - h - 4)
    }
    setPos({ top, left: Math.max(8, r.right - POP_W) })
  }, [open])

  return (
    <>
      {/* The whole permission + re-sharing block IS the trigger — clicking it
          opens the access popover (permission, re-sharing, and Remove). */}
      <div ref={wrapperRef} className="shrink-0">
        <button
          onClick={() => readOnly ? onReadOnlyClick?.() : setOpen(o => !o)}
          aria-label={readOnly ? 'Access managed by the content owner' : 'Edit access'}
          className={`flex items-center gap-1 px-1 py-0.5 rounded transition-colors ${readOnly ? 'cursor-default' : 'themed-bg-hover cursor-pointer'}`}
        >
          <span className="flex flex-col items-center gap-0.5 w-24">
            {/* Read-only cards (reshare-by-you, or an existing recipient a member
                can't manage) drop the link-blue + chevron so they read as a plain
                display, not an editable dropdown. */}
            <span className={`text-sm ${readOnly ? 'themed-text-muted' : 'text-blue-400'}`}>{SHARE_PERM_LABELS[permission]}</span>
            <span className="flex items-center gap-1 text-[11px] themed-text">
              <Share2 size={10} />
              {allowSharing ? 'Allow sharing' : 'No sharing'}
            </span>
          </span>
          {!readOnly && <ChevronDown size={14} className="themed-text-muted shrink-0" />}
        </button>
      </div>
      {open && createPortal(
        // Distinct, elevated surface: the recipient cards are themed-bg-tertiary,
        // so the popover uses the darker themed-bg-primary (+ ring + shadow) to
        // read as a floating menu rather than blending into the card/dialog.
        // No `confirmAction` → instant apply on each click; the parent
        // updates `permission` / `allowSharing` props, which flow back as
        // the new selected state.
        <AccessSettingsPopover
          themeClass={themeClass}
          pos={pos}
          dropdownRef={dropdownRef}
          permissionOptions={options ?? grantableShareOptions(maxPermission)}
          permissionLabels={SHARE_PERM_LABELS}
          selectedPermission={permission}
          disabledOptions={new Set((options ?? grantableShareOptions(maxPermission)).filter(o => RANK[o] > myRank))}
          onSelectPermission={(opt) => onChange(opt as SharePermission, allowSharing)}
          sharingAllowed={allowSharing}
          onSetSharing={(allow) => onChange(permission, allow)}
          removeAction={onRemove ? { label: removeLabel, onClick: () => { setOpen(false); onRemove() } } : undefined}
        />,
        document.body,
      )}
    </>
  )
}
